import type { GameState } from '../state';
import { SAVE_VERSION } from '../state';
import { fromStr, toStr } from '../math/num';
import type { ActiveEnemy } from '../sim/spawn';
import type { PartInstance } from '../data/parts/partTypes';
import { stateDataSchema, type StateData, type SaveBlob } from './schema';
import { migrate } from './migrations';

/** Pure (de)serialization. Decimals become strings; the blob carries version +
 *  savedAt. deserialize is guarded by zod after migration. */

type SE = StateData['combat']['enemies'][number];
type SP = StateData['inventory']['parts'][number];

const serEnemy = (e: ActiveEnemy): SE => ({
  id: e.id,
  visual: { ...e.visual, modifierIds: [...e.visual.modifierIds] },
  name: e.name,
  hp: toStr(e.hp),
  maxHp: toStr(e.maxHp),
  dmgPerSec: toStr(e.dmgPerSec),
  scrapReward: toStr(e.scrapReward),
  componentReward: toStr(e.componentReward),
  isBoss: e.isBoss,
  size: e.size,
  resist: { ...e.resist },
});

const hydEnemy = (e: SE): ActiveEnemy => ({
  id: e.id,
  visual: { ...e.visual, modifierIds: [...e.visual.modifierIds] },
  name: e.name,
  hp: fromStr(e.hp),
  maxHp: fromStr(e.maxHp),
  dmgPerSec: fromStr(e.dmgPerSec),
  scrapReward: fromStr(e.scrapReward),
  componentReward: fromStr(e.componentReward),
  isBoss: e.isBoss,
  size: e.size,
  resist: { ...e.resist },
});

const serPart = (p: PartInstance): SP => ({
  instanceId: p.instanceId,
  defId: p.defId,
  rarity: p.rarity,
  level: p.level,
  affixes: p.affixes.map((a) => ({ stat: a.stat, value: toStr(a.value) })),
});

const hydPart = (p: SP): PartInstance => ({
  instanceId: p.instanceId,
  defId: p.defId,
  rarity: p.rarity,
  level: p.level,
  affixes: p.affixes.map((a) => ({ stat: a.stat, value: fromStr(a.value) })),
});

type SD = StateData['drones'];
const serDrones = (d: GameState['drones']): SD => ({
  unlocked: d.unlocked,
  bots: d.bots,
  broken: d.broken,
  clearance: d.clearance,
  sortie: d.sortie
    ? {
        tier: d.sortie.tier,
        dronesSent: d.sortie.dronesSent,
        endsMs: d.sortie.endsMs,
        status: d.sortie.status,
        result: d.sortie.result
          ? { components: toStr(d.sortie.result.components), cache: d.sortie.result.cache ? serPart(d.sortie.result.cache) : null, broke: d.sortie.result.broke }
          : null,
      }
    : null,
});
const hydDrones = (d: SD): GameState['drones'] => ({
  unlocked: d.unlocked,
  bots: d.bots,
  broken: d.broken,
  clearance: d.clearance,
  sortie: d.sortie
    ? {
        tier: d.sortie.tier,
        dronesSent: d.sortie.dronesSent,
        endsMs: d.sortie.endsMs,
        status: d.sortie.status,
        result: d.sortie.result
          ? { components: fromStr(d.sortie.result.components), cache: d.sortie.result.cache ? hydPart(d.sortie.result.cache) : null, broke: d.sortie.result.broke }
          : null,
      }
    : null,
});

export function serializeState(state: GameState): StateData {
  return {
    version: state.version,
    lastTickAt: state.lastTickAt,
    elapsedMs: state.elapsedMs,
    rng: { seed: state.rng.seed, cursor: state.rng.cursor },
    settings: { ...state.settings },
    wallet: { scrap: toStr(state.wallet.scrap), components: toStr(state.wallet.components), alloy: toStr(state.wallet.alloy) },
    frame: { defId: state.frame.defId, level: state.frame.level, hp: toStr(state.frame.hp) },
    inventory: { parts: state.inventory.parts.map(serPart), nextId: state.inventory.nextId, cap: state.inventory.cap, protectedIds: [...state.inventory.protectedIds] },
    resources: { ...state.resources },
    mind: {
      nodes: state.mind.nodes.map((n) => ({ ...n })),
      edges: state.mind.edges.map((e) => ({ ...e })),
      nextId: state.mind.nextId,
    },
    combat: {
      wave: state.combat.wave,
      highestWave: state.combat.highestWave,
      checkpoint: state.combat.checkpoint,
      enemies: state.combat.enemies.map(serEnemy),
      nextEnemyId: state.combat.nextEnemyId,
      attackTimers: { ...state.combat.attackTimers },
      killsThisRun: state.combat.killsThisRun,
      // Full Send / Maximum Effort cooldowns are transient; not persisted.
      autoAdvance: state.combat.autoAdvance,
      currentDps: toStr(state.combat.currentDps),
      pauseKind: state.combat.pauseKind,
      pauseUntilMs: state.combat.pauseUntilMs,
      overlordId: state.combat.overlordId,
      enrageMs: state.combat.enrageMs,
    },
    stats: { ...state.stats },
    meta: {
      cores: toStr(state.meta.cores),
      ascensions: state.meta.ascensions,
      reboots: state.meta.reboots,
      lifetimeScrap: toStr(state.meta.lifetimeScrap),
      coreUpgrades: { ...state.meta.coreUpgrades },
      unlocks: { ...state.meta.unlocks },
    },
    drones: serDrones(state.drones),
    overlords: { defeated: [...state.overlords.defeated], defeats: { ...state.overlords.defeats } },
    pets: { unlocked: state.pets.unlocked, collection: { ...state.pets.collection }, equipped: [...state.pets.equipped] },
    foundry: { unlocked: state.foundry.unlocked, job: state.foundry.job ? { ...state.foundry.job } : null, fused: state.foundry.fused },
    yard: { unlocked: state.yard.unlocked, level: state.yard.level, trials: state.yard.trials.map((t) => ({ ...t })), trained: state.yard.trained },
    smelter: {
      unlocked: state.smelter.unlocked,
      queue: state.smelter.queue.map((q) => ({ ...q })),
      nextDoneMs: state.smelter.nextDoneMs,
      upgrades: { ...state.smelter.upgrades },
      smelted: state.smelter.smelted,
    },
    rails: {
      unlocked: state.rails.unlocked,
      routes: state.rails.routes.map((r) => ({ ...r })),
      lastDerailMs: state.rails.lastDerailMs,
      built: state.rails.built,
    },
    takeover: {
      unlocked: state.takeover.unlocked,
      stakes: { ...state.takeover.stakes },
      nextPayoutMs: state.takeover.nextPayoutMs,
      lastShiftMs: state.takeover.lastShiftMs,
      lastPayout: toStr(state.takeover.lastPayout),
      payouts: state.takeover.payouts,
    },
    grid: {
      unlocked: state.grid.unlocked,
      run: state.grid.run ? { ...state.grid.run } : null,
      compute: toStr(state.grid.compute),
      upgrades: { ...state.grid.upgrades },
      intrusions: state.grid.intrusions,
    },
    districts: {
      unlocked: state.districts.unlocked,
      baselineWave: state.districts.baselineWave,
      assigned: { ...state.districts.assigned },
      popBonus: state.districts.popBonus,
    },
    harvest: {
      unlocked: state.harvest.unlocked,
      satellites: state.harvest.satellites,
      launch: state.harvest.launch ? { ...state.harvest.launch } : null,
    },
  };
}

export function makeSaveBlob(state: GameState, savedAt: number): SaveBlob {
  return { version: SAVE_VERSION, savedAt, data: serializeState(state) as unknown as Record<string, unknown> };
}

function hydrate(d: StateData): GameState {
  return {
    version: d.version,
    lastTickAt: d.lastTickAt,
    elapsedMs: d.elapsedMs,
    rng: { seed: d.rng.seed, cursor: d.rng.cursor },
    settings: { ...d.settings },
    wallet: { scrap: fromStr(d.wallet.scrap), components: fromStr(d.wallet.components), alloy: fromStr(d.wallet.alloy) },
    frame: { defId: d.frame.defId, level: d.frame.level, hp: fromStr(d.frame.hp) },
    inventory: { parts: d.inventory.parts.map(hydPart), nextId: d.inventory.nextId, cap: d.inventory.cap, protectedIds: [...d.inventory.protectedIds] },
    resources: { ...d.resources },
    mind: {
      nodes: d.mind.nodes.map((n) => ({ ...n })),
      edges: d.mind.edges.map((e) => ({ ...e })),
      nextId: d.mind.nextId,
    },
    combat: {
      wave: d.combat.wave,
      highestWave: d.combat.highestWave,
      checkpoint: d.combat.checkpoint,
      enemies: d.combat.enemies.map(hydEnemy),
      nextEnemyId: d.combat.nextEnemyId,
      attackTimers: { ...d.combat.attackTimers },
      killsThisRun: d.combat.killsThisRun,
      // Transient cooldowns are not stored; every load starts both attacks ready.
      maxEffortUntil: 0,
      maxEffortCdUntil: 0,
      fullSendCdUntil: 0,
      autoAdvance: d.combat.autoAdvance,
      currentDps: fromStr(d.combat.currentDps),
      pauseKind: d.combat.pauseKind,
      pauseUntilMs: d.combat.pauseUntilMs,
      overlordId: d.combat.overlordId,
      enrageMs: d.combat.enrageMs,
    },
    stats: { ...d.stats },
    meta: {
      cores: fromStr(d.meta.cores),
      ascensions: d.meta.ascensions,
      reboots: d.meta.reboots,
      lifetimeScrap: fromStr(d.meta.lifetimeScrap),
      coreUpgrades: { ...d.meta.coreUpgrades },
      unlocks: { ...d.meta.unlocks },
    },
    drones: hydDrones(d.drones),
    overlords: { defeated: [...d.overlords.defeated], defeats: { ...d.overlords.defeats } },
    pets: { unlocked: d.pets.unlocked, collection: { ...d.pets.collection }, equipped: [...d.pets.equipped] },
    foundry: { unlocked: d.foundry.unlocked, job: d.foundry.job ? { ...d.foundry.job } : null, fused: d.foundry.fused },
    yard: { unlocked: d.yard.unlocked, level: d.yard.level, trials: d.yard.trials.map((t) => ({ ...t })), trained: d.yard.trained },
    smelter: {
      unlocked: d.smelter.unlocked,
      queue: d.smelter.queue.map((q) => ({ ...q })),
      nextDoneMs: d.smelter.nextDoneMs,
      upgrades: { ...d.smelter.upgrades },
      smelted: d.smelter.smelted,
    },
    rails: {
      unlocked: d.rails.unlocked,
      routes: d.rails.routes.map((r) => ({ ...r })),
      lastDerailMs: d.rails.lastDerailMs,
      built: d.rails.built,
    },
    takeover: {
      unlocked: d.takeover.unlocked,
      stakes: { ...d.takeover.stakes },
      nextPayoutMs: d.takeover.nextPayoutMs,
      lastShiftMs: d.takeover.lastShiftMs,
      lastPayout: fromStr(d.takeover.lastPayout),
      payouts: d.takeover.payouts,
    },
    grid: {
      unlocked: d.grid.unlocked,
      run: d.grid.run ? { ...d.grid.run } : null,
      compute: fromStr(d.grid.compute),
      upgrades: { ...d.grid.upgrades },
      intrusions: d.grid.intrusions,
    },
    districts: {
      unlocked: d.districts.unlocked,
      baselineWave: d.districts.baselineWave,
      assigned: { ...d.districts.assigned },
      popBonus: d.districts.popBonus,
    },
    harvest: {
      unlocked: d.harvest.unlocked,
      satellites: d.harvest.satellites,
      launch: d.harvest.launch ? { ...d.harvest.launch } : null,
    },
  };
}

export function deserializeBlob(blob: SaveBlob): GameState {
  const { data } = migrate(blob.data, blob.version);
  const parsed = stateDataSchema.parse(data);
  return hydrate(parsed);
}
